import { useBulkJobStore } from "@/features/email-accounts/bulk-job-store";
import { useBulkWarmupSettingsStore } from "@/features/email-accounts/bulk-warmup-settings-store";
import { useDwdSyncStore } from "@/features/dwd-workspace/dwd-sync-store";
import { teardownBetterBugs } from "@/lib/betterbugs";
import { teardownFirebaseAuth } from "@/lib/firebase/auth-ready";
import { teardownHighAdvocacy } from "@/lib/high-advocacy";
import { useUpgradePromptStore } from "@/lib/stores/upgrade-prompt";

/**
 * Transient, per-session Zustand stores that must be reset on EVERY logout
 * path (voluntary `clearAuth` in `lib/auth.ts` and forced `forceLogout` in
 * `lib/api.ts`) — see TRU-1039. None are persisted, but their module-level
 * state otherwise survives a client-side logout → login (no reload tears
 * them down), so the next account to sign in on this tab would see the
 * previous account's tray/hand-off state re-render.
 *
 * Lives in its own module rather than `lib/auth.ts` so `lib/api.ts` can call
 * it without the circular edge `api.ts` -> `auth.ts` -> `api.ts` (`auth.ts`
 * imports `apiFetch` from `api.ts`).
 */
export function clearTransientStores(): void {
  useBulkJobStore.getState().clearActiveJob();
  useDwdSyncStore.getState().clearActiveSync();
  useBulkWarmupSettingsStore.getState().clear();
  // The upgrade prompt is mounted at the app root (`main.tsx`), a SIBLING of
  // `<RouterProvider>` — navigating to `/login` doesn't unmount it. Left open,
  // it renders over the login page and on into the next account's session,
  // still naming the previous user's locked capability.
  useUpgradePromptStore.getState().close();
  // The BetterBugs widget is a document-level overlay outside the router, and
  // its SDK session holds the user's captured screenshot/recording, email, and
  // metadata. Destroy it (not just close it) so none of that carries over onto
  // `/login` or into the next account; `SidebarUserRow` rebuilds and
  // re-syncs identity on the next login.
  teardownBetterBugs();
  // The Firebase session IS the signed-in user (its uid gates every Firestore
  // read), so it must not carry over into the next account on this tab.
  teardownFirebaseAuth();
  // The HighAdvocacy review popup's global config carries the signed-in user.
  teardownHighAdvocacy();
}
